import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";

import Footer from "./Footer";
import MobileFooterSheet from "./MobileFooterSheet";
import PartDetailsWrapper from "./PartDetailsWrapper";

import useBrandsSectionController from "../controllers/useBrandsSectionController";
import useTypedTitle from "../hooks/useTypedTitle";
import useTypedTitleOnce from "../hooks/useTypedTitleOnce";

import Loader from "./Loader";
import TopBar from "./TopBar";
import SearchBar from "./SearchBar";
import BrandStepDesktopCarousel from "./BrandStepDesktopCarousel";
import BrandStepMobileMatrix from "./BrandStepMobileMatrix";
import PartsStepDesktop from "./PartsStepDesktop";
import PartsStepMobile from "./PartsStepMobile";
import RestStepDesktop from "./RestStepDesktop";
import RestStepMobile from "./RestStepMobile";

import SavedCarsModal from "./SavedCarsModal";
import LoginPromptModal from "./LoginPromptModal";
import ViewportZoomController from "../controllers/ViewportZoomController";


import "../styles/BrandsSection.css";

const BrandsSection = ({ onBack, onCartClick, setCurrentView, onGoHome, setZoomAllowed }) => {
  const [selectedPartId, setSelectedPartId] = useState(null);
  const [showPartDetails, setShowPartDetails] = useState(false);

  useEffect(() => {
    // tell App to allow zoom while details overlay is visible
    setZoomAllowed?.(showPartDetails ? true : null);
    return () => setZoomAllowed?.(null); // cleanup on unmount
  }, [showPartDetails, setZoomAllowed]);

  const {
    // data & flags
    brands,
    models,
    years,
    carCategories,
    categories,
    subCategories,
    title,
    prefillBusy,
    prefillApplied,
    atPartCategories,
    searchText,
    activeLoading,
    isBrandStep,
    isMobile,

    // selections
    selectedBrand,
    selectedModel,
    selectedYear,
    selectedSubCategory,
    selectedCarCategory,

    // list & pagination
    list,
    paginatedList,
    currentPage,
    setCurrentPage,
    totalPages,
    canPrev,
    canNext,

    // search & nav
    handleSearchChange,
    handleBackClick,

    // clicks
    handleBrandClick,
    handleGridItemClick,

    // mobile groups & swipes
    brandMobilePageItems,
    brandMobileTotalPages,
    bCanPrev,
    bCanNext,
    handleBrandMobileSwipeEnd,

    restMobilePageItems,
    restMobileTotalPages,
    rCanPrev,
    rCanNext,
    handleRestMobileSwipeEnd,

    partsMobilePageItems,
    partsMobileTotalPages,
    pCanPrev,
    pCanNext,
    handlePartsMobileSwipeEnd,
    selectedCategory,

    // api
    submitPartSuggestion,
    goToBrandStep, goToModelStep, goToYearStep, goToCarCategoryStep, goToPartCategoryStep, goToPartSubCategoryStep
  } = useBrandsSectionController(onBack);

  // Title: type on forward; skip on back (TopBar drops a one-shot hint)
  // Synchronous check: do we have a prefill pending?
  const hasPrefill = (() => {
    try {
      const raw = localStorage.getItem("BRANDS_PREFILL");
      if (!raw) return false;
      const p = JSON.parse(raw);
      return !!p?.brandId;
    } catch { return false; }
  })();


  const displayTitle = useTypedTitleOnce(title, {
    shouldType: true,
    onceKey: title,
  });

  // While prefill is pending (first render) or busy, suppress any default title;
  // otherwise: type only at the Part Categories step, else show plain title.
  const titleForUI = (hasPrefill && !prefillApplied) || prefillBusy
    ? ""
    : (atPartCategories ? displayTitle : displayTitle);

  // Consume "use cache" hint (one-shot) – if present, keep cached lists visible during a refetch
  const useCacheHint = (() => {
    try {
      const v = sessionStorage.getItem("bs_use_cache");
      if (v === "1") {
        sessionStorage.removeItem("bs_use_cache");
        return true;
      }
    } catch { }
    return false;
  })();

  // Simple caches of last visible content
  const [cache, setCache] = useState({
    brandItems: [],
    restItems: [],
    partsItems: [],
    list: [],
    paginatedList: [],
  });

  useEffect(() => {
    if (brandMobilePageItems?.length) setCache((c) => ({ ...c, brandItems: brandMobilePageItems }));
  }, [brandMobilePageItems]);

  useEffect(() => {
    if (restMobilePageItems?.length) setCache((c) => ({ ...c, restItems: restMobilePageItems }));
  }, [restMobilePageItems]);

  useEffect(() => {
    if (partsMobilePageItems?.length) setCache((c) => ({ ...c, partsItems: partsMobilePageItems }));
  }, [partsMobilePageItems]);

  useEffect(() => {
    if (list?.length) setCache((c) => ({ ...c, list }));
  }, [list]);

  useEffect(() => {
    if (paginatedList?.length) setCache((c) => ({ ...c, paginatedList }));
  }, [paginatedList]);

  // Decide what to render: when coming back and refetching, prefer cache
  const preferCache = activeLoading && useCacheHint;

  const brandItemsStable = preferCache ? cache.brandItems : brandMobilePageItems;
  const restItemsStable = preferCache ? cache.restItems : restMobilePageItems;
  const partsItemsStable = preferCache ? cache.partsItems : partsMobilePageItems;

  const listStable = preferCache ? cache.list : list;
  const paginatedListStable = preferCache ? cache.paginatedList : paginatedList;

  const [showSavedCars, setShowSavedCars] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const handleSavedCarsClick = (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    if (!token) setShowLoginPrompt(true);
    else setShowSavedCars(true);
  };

  const handleLogin = () => {
    setShowLoginPrompt(false);
    setCurrentView?.("login");
  };

  const selectedBrandName = brands.find(b => b.id === selectedBrand)?.name;
  const selectedModelName = models.find(m => m.id === selectedModel)?.name;
  const selectedYearName = years.find(y => y.id === selectedYear)?.name;
  const selectedCarCategoryName = carCategories.find(c => c.id === selectedCarCategory)?.name;
  const selectedCategoryName = categories.find(c => c.id === selectedCategory)?.name;
  const selectedSubCategoryName = subCategories.find(s => s.id === selectedSubCategory)?.name;

  const trim = (str, max = 14) => str.length > max ? str.slice(0, max) + "…" : str;

  const steps = [
    selectedBrandName ? trim(selectedBrandName) : "براند السيارة",
    selectedModelName ? trim(selectedModelName) : "الموديل",
    selectedYearName || "السنة",
    selectedCarCategoryName ? trim(selectedCarCategoryName) : "الفئة",
    selectedCategoryName ? trim(selectedCategoryName) : "نوع القطعة",
    selectedSubCategoryName ? trim(selectedSubCategoryName) : "تصنيفها",
    "النتائج",
  ];

  // figure out which step we're on now (1..7)
  const getCurrentStep = () => {
    let s = 1;
    if (selectedBrand) s = 2;
    if (selectedModel) s = 3;
    if (selectedYear) s = 4;

    // Part categories step (before choosing a subcategory)
    if (selectedCarCategory) s = 5;

    if (selectedCategory) s = 6;

    // Subcategory chosen
    if (selectedSubCategory) s = 7;

    // Results: only after subcategory is chosen AND parts have been fetched
    const hasParts =
      (isMobile && (partsItemsStable?.length ?? 0) > 0) ||
      (!isMobile && (paginatedListStable?.length ?? 0) > 0);

    if (selectedSubCategory && hasParts) s = 8;

    return s;
  };


  const currentStep = getCurrentStep();

  // go back to a specific earlier step by calling your "back" handler multiple times
  const jumpBackToStep = (target) => {
    switch (target) {
      case 1: return goToBrandStep();        // الماركة
      case 2: return goToModelStep();        // الموديل
      case 3: return goToYearStep();         // السنة
      case 4: return goToCarCategoryStep();  // نوع المركبة
      case 5: return goToPartCategoryStep(); // تصنيف القطع
      case 6: return goToPartSubCategoryStep(); // التصنيف الفرعي
        
      // step 7 (النتائج) is forward-only; don’t jump there via bar
      default: return;
    }
  };

  const isFinalPartsStep = currentStep === 8;


  if ((hasPrefill && !prefillApplied) || prefillBusy) {
    return (
      <motion.div className="brands-section theme-dark">
        <div className="clip-bg" aria-hidden="true" />
        <TopBar
          onBack={handleBackClick}
          onCartClick={onCartClick}
          activeLoading={true}
          title={"" /* ما نعرض عنوان */}
          onLogoClick={onGoHome}
          isMobile={isMobile}
        />
        <Loader />
      </motion.div>
    );
  }
  // Details view
  if (showPartDetails) {
    return (
      <PartDetailsWrapper
        partId={selectedPartId}
        selectedBrand={brands.find((b) => b.id === selectedBrand)}
        selectedModel={models.find((m) => m.id === selectedModel)}
        selectedYear={years.find((y) => y.id === selectedYear)}
        selectedCategory={carCategories.find((c) => c.id === selectedCarCategory)}
        onBack={() => setShowPartDetails(false)}
        onLogoClick={onGoHome}
        onCartClick={onCartClick}
        setCurrentView={setCurrentView}
      />
    );
  }

  // show loader only if not preferring cache and nothing is on screen yet
  const showLoader = activeLoading && !preferCache &&
    !brandMobilePageItems?.length &&
    !restMobilePageItems?.length &&
    !partsMobilePageItems?.length &&
    !list?.length &&
    !paginatedList?.length;

  return (
    <motion.div
      className="brands-section theme-dark"
      initial={{ opacity: 0, x: 200 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 200 }}
      transition={{ duration: 0.8 }}
    >
      <div className="clip-bg" aria-hidden="true" />

      {/* Top Bar — title animates only on forward path */}
      <TopBar
        onBack={handleBackClick}
        onCartClick={onCartClick}
        activeLoading={activeLoading}
        title={titleForUI}
        onLogoClick={onGoHome}
        isMobile={isMobile}
      />

      {isBrandStep && (
        <div className="container saved-cars-container" style={{ marginTop: 88, marginBottom: 6, textAlign: "center", position: "relative", zIndex: "1000" }} dir="rtl">
          <a
            href=""
            onClick={handleSavedCarsClick}
            style={{ fontSize: 15, color: "#CAEA3E", textDecoration: "underline" }}
          >
            أو انتقل لموترك المحفوظ
          </a>
        </div>
      )}
      {/* Progress bar */}
      <div className={`brands-steps ${isFinalPartsStep ? "parts-brands-step" : ""}`} dir="rtl" aria-label="مراحل اختيار القطع">
        {steps.map((label, idx) => {
          const i = idx + 1;
          const isDoneOrActive = i <= currentStep;
          const isActive = i === currentStep;
          const isClickablePast = i < currentStep;

          return (
            <React.Fragment key={i}>
              <button
                type="button"
                className={`step ${isDoneOrActive ? "is-on" : ""} ${isActive ? "is-active" : ""} ${isClickablePast ? "is-clickable" : "is-disabled"
                  }`}
                onClick={() => isClickablePast && jumpBackToStep(i)}
                title={label}
                aria-current={isActive ? "step" : undefined}
              >
                <span className="dot" title={label} />
                <span className="label">{label}</span>
              </button>

              {i < steps.length && (
                <span className={`bar ${i < currentStep ? "is-on" : ""}`} aria-hidden="true" />
              )}
            </React.Fragment>
          );
        })}
      </div>


      {/* Search (hidden on parts step) */}
      {!selectedSubCategory && (
        <SearchBar value={searchText} onChange={handleSearchChange} disabled={activeLoading && !preferCache} />
      )}

      {showLoader ? (
        <Loader />
      ) : isBrandStep ? (
        // ===== BRAND STEP =====
        isMobile ? (
          <BrandStepMobileMatrix
            items={brandItemsStable}
            currentPage={currentPage}
            totalPages={brandMobileTotalPages}
            canPrev={bCanPrev}
            canNext={bCanNext}
            setCurrentPage={setCurrentPage}
            onSwipeEnd={handleBrandMobileSwipeEnd}
            onBrandClick={handleBrandClick}
            canShow={true}
          />
        ) : (
          <>
            {(brandItemsStable?.length || listStable?.length) && (
              <BrandStepDesktopCarousel list={listStable} onBrandClick={handleBrandClick} />
            )}
          </>
        )
      ) : selectedSubCategory ? (
        // ===== PARTS STEP =====
        isMobile ? (
          <PartsStepMobile
            items={partsItemsStable}
            currentPage={currentPage}
            totalPages={partsMobileTotalPages}
            canPrev={pCanPrev}
            canNext={pCanNext}
            setCurrentPage={setCurrentPage}
            onSwipeEnd={handlePartsMobileSwipeEnd}
            onPartSelect={(id) => {
              setSelectedPartId(id);
              setShowPartDetails(true);
            }}
            onSuggest={submitPartSuggestion}
            isMobile={isMobile}
            showSuggest={true}
            setCurrentView={setCurrentView}
          />
        ) : (
          <>
            {(paginatedListStable?.length || listStable?.length) && (
              <PartsStepDesktop
                paginatedList={paginatedListStable}
                listLength={listStable.length}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                onPartSelect={(id) => {
                  setSelectedPartId(id);
                  setShowPartDetails(true);
                }}
                onSuggest={submitPartSuggestion}
                setCurrentView={setCurrentView}
              />
            )}
          </>
        )
      ) : (
        // ===== OTHER STEPS =====
        isMobile ? (
          <RestStepMobile
            items={restItemsStable}
            currentPage={currentPage}
            totalPages={restMobileTotalPages}
            canPrev={rCanPrev}
            canNext={rCanNext}
            setCurrentPage={setCurrentPage}
            onSwipeEnd={handleRestMobileSwipeEnd}
            onItemClick={handleGridItemClick}
            canShow={true}
          />
        ) : (
          <>
            {(paginatedListStable?.length || listStable?.length) && (
              <RestStepDesktop
                paginatedList={paginatedListStable}
                canPrev={canPrev}
                canNext={canNext}
                currentPage={currentPage}
                totalPages={totalPages}
                setCurrentPage={setCurrentPage}
                onItemClick={handleGridItemClick}
              />
            )}
          </>
        )
      )}


      {/* MODALS */}
      <SavedCarsModal
        open={showSavedCars}
        onClose={() => setShowSavedCars(false)}
        setCurrentView={setCurrentView}
        onAddCar={() => setCurrentView?.("brands")}
      />
      <LoginPromptModal
        open={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        onLogin={handleLogin}
      />

      
      <div className="hero">
        <MobileFooterSheet />
      </div>

    </motion.div>
  );
};

export default BrandsSection;
